import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Modal from 'react-modal';
import { startLoadingConfigurations } from '../../app/actions/configurationActions';
import {
  startLoadSupportFormDetail,
  startLoadingSupportForms,
  startReplaceSupportFormQuestions,
  startSeedSupportFormCatalog,
  startSetSupportFormMode,
  startToggleSupportFormEnabled,
} from '../../app/actions/supportFormActions';
import { getSupportFormMode } from '../../app/helpers/semanticSearchConfigHelper';
import { MAX_SUPPORT_QUESTIONS, MODULE_KEYS } from '../end-user-search/supportFormCatalog';
import SupportRequestForm from '../end-user-search/SupportRequestForm';

Modal.setAppElement('#root');

const modalStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    width: '96%',
    maxWidth: '880px',
    maxHeight: '92vh',
    overflowY: 'auto',
  },
  overlay: { backgroundColor: 'rgba(0,0,0,0.55)', zIndex: 3200 },
};

const optionsToText = (options) =>
  (options || [])
    .map((opt) => {
      const value = opt.value || '';
      const label = opt.label || '';
      const labelKey = opt.labelKey || opt.label_i18n_key || '';
      if (labelKey) return `${value}|${label}|${labelKey}`;
      if (label) return `${value}|${label}`;
      return value;
    })
    .join('\n');

const textToOptions = (text) =>
  String(text || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const parts = line.split('|');
      const value = (parts[0] || '').trim();
      const label = (parts[1] || value).trim();
      const labelKey = (parts[2] || '').trim();
      return { value, label, labelKey };
    });

const questionToDraft = (q, index) => ({
  key: q.key || `q${index + 1}`,
  type: q.type || 'single_select',
  label: q.label || '',
  labelKey: q.labelKey || '',
  required: !!q.required,
  optionsText: optionsToText(q.options),
});

const blankQuestion = (index) => ({
  key: `question_${index + 1}`,
  type: index === 0 ? 'single_select' : 'open_text',
  label: '',
  labelKey: '',
  required: index === 0,
  optionsText: index === 0 ? 'opt1|Opción 1' : '',
});

const AdminSupportFormsScreen = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const configuration = useSelector((state) => state.configuration);
  const { forms, selectedForm, loading } = useSelector((state) => state.supportForm);
  const formMode = getSupportFormMode(configuration);

  const [editOpen, setEditOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [draft, setDraft] = useState([]);
  const [previewAnswers, setPreviewAnswers] = useState({});
  const [previewModule, setPreviewModule] = useState('');

  useEffect(() => {
    dispatch(startLoadingConfigurations());
    dispatch(startLoadingSupportForms());
  }, [dispatch]);

  const rows = useMemo(() => {
    const byModule = {};
    (forms || []).forEach((form) => {
      const key = form.module_key;
      if (!byModule[key] || Number(form.version) > Number(byModule[key].version)) {
        byModule[key] = form;
      }
    });
    return MODULE_KEYS.map((moduleKey) => byModule[moduleKey] || null);
  }, [forms]);

  const moduleLabel = (key) => t(`semanticSearch.module.${key}`, { defaultValue: key });

  const openEditor = async (form) => {
    const body = await dispatch(startLoadSupportFormDetail(form.id));
    const questions = (body && body.form && body.form.questions) || form.questions || [];
    setDraft(questions.length ? questions.map(questionToDraft) : [blankQuestion(0)]);
    setEditOpen(true);
  };

  const openPreview = async (form) => {
    const body = await dispatch(startLoadSupportFormDetail(form.id));
    const questions = (body && body.form && body.form.questions) || form.questions || [];
    setPreviewModule(form.module_key);
    setPreviewAnswers({});
    setDraft(questions.map(questionToDraft));
    setPreviewOpen(true);
  };

  const previewQuestions = draft.map((q) => ({
    key: q.key,
    type: q.type,
    label: q.label,
    labelKey: q.labelKey,
    required: q.required,
    options: q.type === 'open_text' ? [] : textToOptions(q.optionsText),
  }));

  const onSaveQuestions = async () => {
    if (!selectedForm || !selectedForm.id) return;
    const questions = previewQuestions.map((q, index) => ({
      ...q,
      sequence: index + 1,
    }));
    const body = await dispatch(startReplaceSupportFormQuestions(selectedForm.id, questions));
    if (body && body.ok) setEditOpen(false);
  };

  const updateDraft = (index, patch) => {
    setDraft((prev) => prev.map((item, i) => (i === index ? { ...item, ...patch } : item)));
  };

  return (
    <div className="container mt-4">
      <h2>{t('semanticSearch.formAdmin.title', { defaultValue: 'Preguntas de soporte' })}</h2>
      <p className="text-muted">
        {t('semanticSearch.formAdmin.subtitle', {
          defaultValue:
            'Catálogo por módulo (hasta 3 de opción y 1 abierto). El popup de Búsqueda usa el modo activo. El contacto no se edita aquí.',
        })}
      </p>

      <div className="card mb-3">
        <div className="card-body">
          <label className="form-label fw-semibold">
            {t('semanticSearch.formAdmin.modeLabel', { defaultValue: 'Modo del popup' })}
          </label>
          <div className="d-flex flex-wrap gap-3 mb-2">
            {['static', 'hybrid', 'dynamic'].map((mode) => (
              <label className="form-check" key={mode}>
                <input
                  className="form-check-input"
                  type="radio"
                  name="support-form-mode"
                  checked={formMode === mode}
                  onChange={() => dispatch(startSetSupportFormMode(mode))}
                />
                <span className="form-check-label">
                  {t(`semanticSearch.formAdmin.mode.${mode}`, { defaultValue: mode })}
                </span>
              </label>
            ))}
          </div>
          <p className="small text-muted mb-0">
            {t('semanticSearch.formAdmin.modeHint', {
              defaultValue:
                'static = una pregunta abierta. hybrid = catálogo en código. dynamic = estas tablas (editable sin deploy). Recargue Búsqueda tras cambiar el modo.',
            })}
          </p>
        </div>
      </div>

      <div className="d-flex flex-wrap gap-2 mb-3">
        <button
          type="button"
          className="btn btn-outline-primary"
          onClick={() => dispatch(startSeedSupportFormCatalog())}
        >
          {t('semanticSearch.formAdmin.seed', { defaultValue: 'Copiar catálogo híbrido a las tablas' })}
        </button>
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={() => dispatch(startLoadingSupportForms())}
        >
          {t('semanticSearch.formAdmin.refresh', { defaultValue: 'Actualizar lista' })}
        </button>
      </div>

      {loading ? (
        <p>{t('semanticSearch.admin.loading', { defaultValue: 'Cargando…' })}</p>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm table-striped align-middle">
            <thead>
              <tr>
                <th>{t('semanticSearch.formAdmin.col.module', { defaultValue: 'Módulo' })}</th>
                <th>{t('semanticSearch.formAdmin.col.version', { defaultValue: 'Versión' })}</th>
                <th>{t('semanticSearch.formAdmin.col.enabled', { defaultValue: 'Activo' })}</th>
                <th>{t('semanticSearch.formAdmin.col.questions', { defaultValue: 'Preguntas' })}</th>
                <th>{t('semanticSearch.formAdmin.col.actions', { defaultValue: 'Acciones' })}</th>
              </tr>
            </thead>
            <tbody>
              {MODULE_KEYS.map((moduleKey, idx) => {
                const form = rows[idx];
                return (
                  <tr key={moduleKey}>
                    <td>{moduleLabel(moduleKey)}</td>
                    <td>{form ? form.version : '—'}</td>
                    <td>
                      {form
                        ? form.enabled
                          ? t('semanticSearch.formAdmin.yes', { defaultValue: 'Sí' })
                          : t('semanticSearch.formAdmin.no', { defaultValue: 'No' })
                        : t('semanticSearch.formAdmin.missing', { defaultValue: 'Sin formulario' })}
                    </td>
                    <td>{form ? (form.questions || []).length : 0}</td>
                    <td>
                      {form ? (
                        <div className="d-flex flex-wrap gap-1">
                          <button type="button" className="btn btn-sm btn-primary" onClick={() => openEditor(form)}>
                            {t('semanticSearch.formAdmin.edit', { defaultValue: 'Editar preguntas' })}
                          </button>
                          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => openPreview(form)}>
                            {t('semanticSearch.formAdmin.preview', { defaultValue: 'Probar popup' })}
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-dark"
                            onClick={() => dispatch(startToggleSupportFormEnabled(form))}
                          >
                            {form.enabled
                              ? t('semanticSearch.formAdmin.disable', { defaultValue: 'Desactivar' })
                              : t('semanticSearch.formAdmin.enable', { defaultValue: 'Activar' })}
                          </button>
                        </div>
                      ) : (
                        <span className="text-muted">
                          {t('semanticSearch.formAdmin.useSeed', {
                            defaultValue: 'Use Copiar catálogo para crear los 8 módulos.',
                          })}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        isOpen={editOpen}
        onRequestClose={() => setEditOpen(false)}
        style={modalStyles}
        contentLabel={t('semanticSearch.formAdmin.editTitle', { defaultValue: 'Editar preguntas' })}
      >
        <h4>
          {t('semanticSearch.formAdmin.editTitle', { defaultValue: 'Editar preguntas' })}
          {selectedForm ? ` — ${moduleLabel(selectedForm.module_key)}` : ''}
        </h4>
        <p className="small text-muted">
          {t('semanticSearch.formAdmin.optionsHint', {
            defaultValue: 'Opciones: una por línea, formato valor|etiqueta|clave_i18n (la clave es opcional). Máximo 4 preguntas.',
          })}
        </p>
        {draft.map((q, index) => (
          <div className="border rounded p-2 mb-2" key={`draft-${index}`}>
            <div className="row g-2">
              <div className="col-md-3">
                <label className="form-label">{t('semanticSearch.formAdmin.key', { defaultValue: 'Clave' })}</label>
                <input className="form-control" value={q.key} onChange={(e) => updateDraft(index, { key: e.target.value })} />
              </div>
              <div className="col-md-3">
                <label className="form-label">{t('semanticSearch.formAdmin.type', { defaultValue: 'Tipo' })}</label>
                <select className="form-select" value={q.type} onChange={(e) => updateDraft(index, { type: e.target.value })}>
                  <option value="single_select">single_select</option>
                  <option value="multi_select">multi_select</option>
                  <option value="open_text">open_text</option>
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label">{t('semanticSearch.formAdmin.i18n', { defaultValue: 'Clave i18n' })}</label>
                <input
                  className="form-control"
                  value={q.labelKey}
                  onChange={(e) => updateDraft(index, { labelKey: e.target.value })}
                />
              </div>
              <div className="col-md-3 d-flex align-items-end">
                <label className="form-check mb-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={q.required}
                    onChange={(e) => updateDraft(index, { required: e.target.checked })}
                  />
                  <span className="form-check-label">
                    {t('semanticSearch.formAdmin.required', { defaultValue: 'Obligatoria' })}
                  </span>
                </label>
              </div>
              <div className="col-12">
                <label className="form-label">{t('semanticSearch.formAdmin.label', { defaultValue: 'Etiqueta' })}</label>
                <input className="form-control" value={q.label} onChange={(e) => updateDraft(index, { label: e.target.value })} />
              </div>
              {q.type !== 'open_text' ? (
                <div className="col-12">
                  <label className="form-label">{t('semanticSearch.formAdmin.options', { defaultValue: 'Opciones' })}</label>
                  <textarea
                    className="form-control"
                    rows={4}
                    value={q.optionsText}
                    onChange={(e) => updateDraft(index, { optionsText: e.target.value })}
                  />
                </div>
              ) : null}
              <div className="col-12">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  disabled={draft.length <= 1}
                  onClick={() => setDraft((prev) => prev.filter((_, i) => i !== index))}
                >
                  {t('semanticSearch.formAdmin.remove', { defaultValue: 'Quitar pregunta' })}
                </button>
              </div>
            </div>
          </div>
        ))}
        <button
          type="button"
          className="btn btn-outline-primary mb-3"
          disabled={draft.length >= MAX_SUPPORT_QUESTIONS}
          onClick={() => setDraft((prev) => [...prev, blankQuestion(prev.length)])}
        >
          {t('semanticSearch.formAdmin.add', { defaultValue: 'Añadir pregunta' })}
        </button>
        <div className="d-flex justify-content-end gap-2">
          <button type="button" className="btn btn-secondary" onClick={() => setEditOpen(false)}>
            {t('buttons.close', { defaultValue: 'Cerrar' })}
          </button>
          <button type="button" className="btn btn-primary" onClick={onSaveQuestions}>
            {t('semanticSearch.formAdmin.save', { defaultValue: 'Guardar preguntas' })}
          </button>
        </div>
      </Modal>

      <Modal
        isOpen={previewOpen}
        onRequestClose={() => setPreviewOpen(false)}
        style={modalStyles}
        contentLabel={t('semanticSearch.formAdmin.previewTitle', { defaultValue: 'Vista del popup' })}
      >
        <h4>
          {t('semanticSearch.formAdmin.previewTitle', { defaultValue: 'Vista del popup' })}
          {previewModule ? ` — ${moduleLabel(previewModule)}` : ''}
        </h4>
        <p className="small text-muted">
          {t('semanticSearch.formAdmin.previewHint', {
            defaultValue: 'Solo muestra las preguntas. El nombre, canal y consentimiento siguen en el popup de Búsqueda.',
          })}
        </p>
        <SupportRequestForm questions={previewQuestions} answers={previewAnswers} onChange={setPreviewAnswers} />
        <div className="d-flex justify-content-end mt-3">
          <button type="button" className="btn btn-secondary" onClick={() => setPreviewOpen(false)}>
            {t('buttons.close', { defaultValue: 'Cerrar' })}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default AdminSupportFormsScreen;
